import {
  BODY_POSITIONS,
  EQUIPMENT,
  EXERCISE_CATEGORIES,
  EXERCISE_DIFFICULTIES,
  IMPACTS,
  LATERALITIES,
  MECHANICS,
  MOVEMENT_PATTERNS,
  MUSCLE_GROUPS,
  type EquipmentName,
  type ExerciseCategoryName,
  type ExerciseDifficultyName,
  type MuscleGroupName,
} from '@steado/admin-contract/exercises';
import { INJURY_AREAS, type InjuryAreaName } from '@steado/admin-contract/user-detail';
import { useId } from 'react';

import { Labelled, NATIVE_SELECT, NullableSelect, TokenPicker } from '@/components/form/fields';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Panel } from '@/components/panel/panels';

import { toggle, type ExerciseFormValues } from './exercise-form';

interface ExerciseFieldsProps {
  readonly values: ExerciseFormValues;
  readonly onChange: (next: ExerciseFormValues) => void;
  /** False for `support`, which reads the library and changes nothing. */
  readonly disabled: boolean;
}

/**
 * Every editable field of an exercise, once.
 *
 * The create page and the detail page's Overview tab are the same form against
 * different endpoints, so they are the same component: a field added to one and
 * forgotten on the other is exactly how the modal this replaced ended up
 * unable to set half the row.
 */
export function ExerciseFields({ values, onChange, disabled }: ExerciseFieldsProps) {
  const nameId = useId();
  const categoryId = useId();
  const difficultyId = useId();
  const sortId = useId();
  const stepsId = useId();
  const weightedId = useId();

  const set = <K extends keyof ExerciseFormValues>(key: K, value: ExerciseFormValues[K]) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="flex flex-col gap-4">
      <Panel title="Movement">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
            <Labelled id={nameId} label="Name">
              <Input
                id={nameId}
                value={values.name}
                disabled={disabled}
                onChange={(event) => {
                  set('name', event.target.value);
                }}
              />
            </Labelled>

            <Labelled id={categoryId} label="Category">
              <select
                id={categoryId}
                value={values.category}
                disabled={disabled}
                onChange={(event) => {
                  set('category', event.target.value as ExerciseCategoryName);
                }}
                className={NATIVE_SELECT}
              >
                {EXERCISE_CATEGORIES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Labelled>

            <Labelled id={difficultyId} label="Difficulty">
              <select
                id={difficultyId}
                value={values.difficulty}
                disabled={disabled}
                onChange={(event) => {
                  set('difficulty', event.target.value as ExerciseDifficultyName);
                }}
                className={NATIVE_SELECT}
              >
                {EXERCISE_DIFFICULTIES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Labelled>

            <Labelled id={sortId} label="Sort order" hint="The app’s display order. Ties are fine.">
              <Input
                id={sortId}
                type="number"
                min={0}
                value={values.sortOrder}
                disabled={disabled}
                onChange={(event) => {
                  set('sortOrder', event.target.value);
                }}
              />
            </Labelled>
          </div>

          <div className="flex items-center gap-3">
            <Checkbox
              id={weightedId}
              checked={values.isWeighted}
              disabled={disabled}
              onCheckedChange={(checked) => {
                set('isWeighted', checked === true);
              }}
            />
            <Label htmlFor={weightedId} className="font-normal text-foreground">
              Load is meaningful (bodyweight and cardio work have no 1RM)
            </Label>
          </div>
        </div>
      </Panel>

      <Panel title="Muscles">
        <div className="flex flex-col gap-5">
          <TokenPicker
            label="Primary muscles"
            hint="What the movement is for. Plan generation selects on this."
            options={MUSCLE_GROUPS}
            selected={values.primaryMuscles}
            disabled={disabled}
            onToggle={(value: MuscleGroupName) => {
              set('primaryMuscles', toggle(values.primaryMuscles, value));
            }}
          />
          <TokenPicker
            label="Secondary muscles"
            hint="Worked, but not the point of the movement."
            options={MUSCLE_GROUPS}
            selected={values.secondaryMuscles}
            disabled={disabled}
            onToggle={(value: MuscleGroupName) => {
              set('secondaryMuscles', toggle(values.secondaryMuscles, value));
            }}
          />
        </div>
      </Panel>

      <Panel title="Equipment and safety">
        <div className="flex flex-col gap-5">
          <TokenPicker
            label="Equipment"
            options={EQUIPMENT}
            selected={values.equipment}
            disabled={disabled}
            onToggle={(value: EquipmentName) => {
              set('equipment', toggle(values.equipment, value));
            }}
          />
          <TokenPicker
            label="Contraindications"
            hint="Joints this loads enough to be unsafe on. Intersected with a user’s injury flags to exclude it."
            options={INJURY_AREAS}
            selected={values.contraindications}
            disabled={disabled}
            onToggle={(value: InjuryAreaName) => {
              set('contraindications', toggle(values.contraindications, value));
            }}
          />
        </div>
      </Panel>

      <Panel title="Behaviour">
        <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">
          How the movement behaves, as opposed to what it trains.{' '}
          <strong className="font-medium text-muted-foreground">Unassessed is a real answer.</strong> The
          import fills in only what the dataset states and what a name rule can decide, so a blank here means
          nobody has looked, not that the answer is no. Mechanic and laterality set the load step a
          progression suggestion uses; the spine and impact fields are what an injury restriction reads.
          Position, trunk flexion and the pregnancy flag, with impact and difficulty, are what decide whether
          a pregnant or nursing user is offered the movement. A movement that needs a partner is never put in
          anyone’s plan; a blank there reads as one a person can do alone.
        </p>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
          <NullableSelect
            label="Mechanic"
            hint="Isolation work steps up in smaller jumps."
            options={MECHANICS}
            value={values.mechanic}
            disabled={disabled}
            onChange={(next) => {
              set('mechanic', next);
            }}
          />
          <NullableSelect
            label="Movement pattern"
            hint="What a week’s coverage is balanced over."
            options={MOVEMENT_PATTERNS}
            value={values.movementPattern}
            disabled={disabled}
            onChange={(next) => {
              set('movementPattern', next);
            }}
          />
          <NullableSelect
            label="Laterality"
            hint="Unilateral load is per limb, so the step halves."
            options={LATERALITIES}
            value={values.laterality}
            disabled={disabled}
            onChange={(next) => {
              set('laterality', next);
            }}
          />
          <NullableSelect
            label="Impact"
            hint="What it asks of joints on landing."
            options={IMPACTS}
            value={values.impact}
            disabled={disabled}
            onChange={(next) => {
              set('impact', next);
            }}
          />
          <NullableSelect
            label="Axial load"
            hint="Spine under external load: a bar on the back or overhead."
            options={YES_NO_OPTIONS}
            value={yesNo(values.axialLoad)}
            disabled={disabled}
            onChange={(next) => {
              set('axialLoad', next === null ? null : next === 'yes');
            }}
          />
          <NullableSelect
            label="Body position"
            hint="Quadruped is hands and knees or a plank; prone is belly on the floor."
            options={BODY_POSITIONS}
            value={values.bodyPosition}
            disabled={disabled}
            onChange={(next) => {
              set('bodyPosition', next);
            }}
          />
          <NullableSelect
            label="Trunk flexion"
            hint="Curls the trunk against load or gravity: a sit-up, a crunch."
            options={YES_NO_OPTIONS}
            value={yesNo(values.trunkFlexion)}
            disabled={disabled}
            onChange={(next) => {
              set('trunkFlexion', next === null ? null : next === 'yes');
            }}
          />
          <NullableSelect
            label="Avoid in pregnancy"
            hint="Deep twists, backbends, inversions, belly compression, Olympic lifts, fall risk."
            options={YES_NO_OPTIONS}
            value={yesNo(values.avoidInPregnancy)}
            disabled={disabled}
            onChange={(next) => {
              set('avoidInPregnancy', next === null ? null : next === 'yes');
            }}
          />
          <NullableSelect
            label="Needs a partner"
            hint="Someone else holds, resists or hands it over. Kept out of generated plans."
            options={YES_NO_OPTIONS}
            value={yesNo(values.requiresPartner)}
            disabled={disabled}
            onChange={(next) => {
              set('requiresPartner', next === null ? null : next === 'yes');
            }}
          />
        </div>
      </Panel>

      <Panel title="How to">
        <Labelled id={stepsId} label="One cue per line" hint="The app renders them as a numbered list.">
          <Textarea
            id={stepsId}
            value={values.howToSteps}
            rows={5}
            disabled={disabled}
            onChange={(event) => {
              set('howToSteps', event.target.value);
            }}
            className="leading-relaxed"
          />
        </Labelled>
      </Panel>
    </div>
  );
}

/** Yes / no / nobody has said, as a three-valued select rather than a checkbox. */
const YES_NO_OPTIONS = ['yes', 'no'] as const;

function yesNo(value: boolean | null): (typeof YES_NO_OPTIONS)[number] | null {
  return value === null ? null : value ? 'yes' : 'no';
}
